import { useState } from 'react';
import { CollegeSections } from '../components/partners/CollegeSections';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ForColleges = () => {
  const [formData, setFormData] = useState({
    institution_name: '',
    contact_person: '',
    designation: '',
    official_email: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/college-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit inquiry');
      }

      setSubmitSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <CollegeSections />

      <section id="partnership-form" className="py-20 lg:py-28 bg-[#0b1f4d] scroll-mt-20">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 scroll-mt-24">
          <div className="text-center mb-8">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-3">
              Explore a Partnership
            </h2>
            <p className="text-slate-400">
              Share your details and we'll connect to discuss how we can support your students.
            </p>
          </div>

          {submitSuccess ? (
            <div className="bg-teal-900/30 border border-teal-500/30 rounded-2xl p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-teal-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Thank you for your interest!</h3>
              <p className="text-slate-300">
                We'll review your inquiry and connect with you shortly to discuss partnership options.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  name="institution_name"
                  value={formData.institution_name}
                  onChange={handleInputChange}
                  placeholder="Institution Name"
                  required
                  className="bg-white/10 border-slate-700 text-white placeholder:text-slate-500 rounded-xl h-12"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="text"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleInputChange}
                  placeholder="Your Name"
                  required
                  className="bg-white/10 border-slate-700 text-white placeholder:text-slate-500 rounded-xl h-12"
                />
                <Input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                  placeholder="Designation"
                  required
                  className="bg-white/10 border-slate-700 text-white placeholder:text-slate-500 rounded-xl h-12"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="email"
                  name="official_email"
                  value={formData.official_email}
                  onChange={handleInputChange}
                  placeholder="Official Email"
                  required
                  className="bg-white/10 border-slate-700 text-white placeholder:text-slate-500 rounded-xl h-12"
                />
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                  required
                  className="bg-white/10 border-slate-700 text-white placeholder:text-slate-500 rounded-xl h-12"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-400 text-[#0b1f4d] hover:bg-teal-300 py-6 rounded-full font-semibold text-lg mt-4"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Explore a Partnership
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ForColleges;
